/**
 * GroVita Section Controller — Wokiee Edition
 * Architecture: Section-Scoped Lifecycle Management
 *
 * Manages initialization and destruction of section-specific logic
 * to prevent memory leaks and ensure Theme Editor compatibility.
 *
 * Sections:
 *   - grovita-hero       → Banner entrance animations
 *   - grovita-header     → Scroll pill + mobile menu
 *   - grovita-cta        → CTA banner entrance
 *   - grovita-features          → Trust features reveal
 *   - grovita-newsletter         → Newsletter form handling
 *   - grovita-horizontal-gallery → Featured products grid entrance
 *   - grovita-category-explorer  → Category cards stagger reveal
 *   - grovita-combo-reveal       → Saucer + Pot scroll storytelling (GSAP ScrollTrigger)
 *
 * Global Systems:
 *   - GrovitaWishlist        → localStorage wishlist
 *   - GrovitaWishlistDrawer  → Wishlist sidebar drawer
 *   - AJAX Add-to-Cart       → Section Rendering API integration
 *   - Cart Icon              → Drawer open + PubSub sync
 *   - Reveal Observer        → CSS-based entrance animations
 */

const GrovitaTheme = {
  sections: {},

  register: function(type, container) {
    if (this.sections[type]) {
      console.log(`[GroVita] Initializing section: ${type}`);
      try {
        this.sections[type].init(container);
        container.dataset.grovitaInitialized = 'true';
      } catch (e) {
        console.error(`[GroVita] Error initializing ${type}:`, e);
      }
    }
  },

  unload: function(type, container) {
    if (this.sections[type] && this.sections[type].destroy) {
      console.log(`[GroVita] Destroying section: ${type}`);
      try {
        this.sections[type].destroy(container);
        delete container.dataset.grovitaInitialized;
      } catch (e) {
        console.error(`[GroVita] Error destroying ${type}:`, e);
      }
    }
  },

  prefersReducedMotion: () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  isDesktop: () => window.innerWidth >= 750
};

// =========================================
// GLOBAL REVEAL OBSERVER
// Adds .is-visible to elements with .grovita-reveal or .grovita-reveal-stagger
// when they enter the viewport. Pure CSS transitions handle the animation.
// =========================================

function grovitaInitRevealObserver(scope) {
  if (GrovitaTheme.prefersReducedMotion()) return;
  const root = scope || document;
  const targets = root.querySelectorAll('.grovita-reveal, .grovita-reveal-stagger');
  if (!targets.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

  targets.forEach(el => observer.observe(el));
  return observer;
}

// =========================================
// SECTION: HERO BANNER — Product Carousel
// Dynamic text + depth carousel with blur
// =========================================

GrovitaTheme.sections['grovita-hero'] = {
  init: (container) => {
    const slides    = container.querySelectorAll('[data-hero-slide]');
    const textPanes = container.querySelectorAll('[data-hero-text]');
    const dots      = container.querySelectorAll('[data-hero-dot]');
    const prevBtn   = container.querySelector('[data-hero-prev]');
    const nextBtn   = container.querySelector('[data-hero-next]');
    const total     = slides.length;

    if (total === 0) return;

    let current    = 0;
    let autoplayId = null;
    let isAnimating = false;

    // --- Helpers ---
    const mod = (n, m) => ((n % m) + m) % m;

    function applySlideStates(index) {
      const prevIdx = mod(index - 1, total);
      const nextIdx = mod(index + 1, total);

      slides.forEach((s, i) => {
        s.classList.remove('is-active', 'is-prev', 'is-next');
        if (i === index)   s.classList.add('is-active');
        else if (i === prevIdx) s.classList.add('is-prev');
        else if (i === nextIdx) s.classList.add('is-next');
      });

      textPanes.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
      });

      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === index);
      });
    }

    function goTo(index) {
      if (isAnimating || index === current) return;
      isAnimating = true;
      current = mod(index, total);
      applySlideStates(current);
      // Release lock after CSS transition
      setTimeout(() => { isAnimating = false; }, 680);
    }

    // --- Arrow clicks ---
    if (prevBtn) prevBtn.addEventListener('click', () => goTo(current - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(current + 1));

    // --- Dot clicks ---
    dots.forEach(d => {
      d.addEventListener('click', () => {
        goTo(parseInt(d.dataset.heroDot, 10));
      });
    });

    // --- Touch / swipe support ---
    // Bind to the whole section so the entire hero is swipeable on mobile,
    // not just the image carousel area.
    let touchStartX = 0;
    let touchStartY = 0;

    container.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].clientX;
      touchStartY = e.changedTouches[0].clientY;
    }, { passive: true });

    container.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) goTo(current + 1);
        else         goTo(current - 1);
      }
    }, { passive: true });

    // --- Autoplay ---
    const autoplay  = container.dataset.autoplay === 'true';
    const speed     = (parseInt(container.dataset.autoplaySpeed, 10) || 5) * 1000;

    function startAutoplay() {
      if (!autoplay) return;
      stopAutoplay();
      autoplayId = setInterval(() => goTo(current + 1), speed);
    }

    function stopAutoplay() {
      if (autoplayId) { clearInterval(autoplayId); autoplayId = null; }
    }

    // Pause when user interacts
    container.addEventListener('pointerenter', stopAutoplay);
    container.addEventListener('pointerleave', startAutoplay);

    // --- Entrance animation ---
    if (!GrovitaTheme.prefersReducedMotion()) {
      container.classList.add('is-entering');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          container.classList.remove('is-entering');
        });
      });
    }

    // --- Keyboard support ---
    container.setAttribute('tabindex', '0');
    container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  goTo(current - 1);
      if (e.key === 'ArrowRight') goTo(current + 1);
    });

    // Initial state
    applySlideStates(current);
    startAutoplay();

    // Store cleanup refs
    container._heroCarousel = { stopAutoplay };
  },

  destroy: (container) => {
    if (container._heroCarousel) {
      container._heroCarousel.stopAutoplay();
    }
  }
};

// =========================================
// SECTION: HEADER — Scroll + Mobile Menu
// =========================================

GrovitaTheme.sections['grovita-header'] = {
  init: (container) => {
    const threshold = parseInt(container.dataset.scrollThreshold, 10) || 50;
    const hideOnScroll = container.dataset.hideOnScroll === 'true';
    let prevY = window.scrollY || 0;
    const HIDE_AT = 150;

    const handleScroll = () => {
      const y = window.scrollY || 0;

      if (y > threshold) {
        container.classList.add('scrolled');
      } else {
        container.classList.remove('scrolled');
      }

      if (hideOnScroll) {
        if (y > prevY && y > HIDE_AT) {
          container.classList.add('is-hidden');
        } else {
          container.classList.remove('is-hidden');
        }
      }

      prevY = y;
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    container._scrollHandler = handleScroll;

    // Mobile Menu
    const toggleBtn = container.querySelector('[data-grovita-menu-toggle]');
    const menu = container.querySelector('.grovita-mobile-menu');
    const closeBtns = container.querySelectorAll('[data-grovita-menu-close]');

    if (toggleBtn && menu) {
      const openMenu = () => {
        menu.setAttribute('aria-hidden', 'false');
        toggleBtn.setAttribute('aria-expanded', 'true');
        document.body.classList.add('overflow-hidden');
        const firstLink = menu.querySelector('.grovita-mobile-menu__link');
        if (firstLink) setTimeout(() => firstLink.focus(), 450);
      };

      const closeMenu = () => {
        menu.setAttribute('aria-hidden', 'true');
        toggleBtn.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('overflow-hidden');
        toggleBtn.focus();
      };

      toggleBtn.addEventListener('click', () => {
        const isOpen = menu.getAttribute('aria-hidden') === 'false';
        isOpen ? closeMenu() : openMenu();
      });

      closeBtns.forEach(btn => btn.addEventListener('click', closeMenu));

      // Robust outside-click: close when clicking anywhere outside the menu panel.
      document.addEventListener('click', (e) => {
        if (menu.getAttribute('aria-hidden') !== 'false') return;
        const panel = menu.querySelector('.grovita-mobile-menu__panel');
        if (panel && !panel.contains(e.target) && !toggleBtn.contains(e.target)) {
          closeMenu();
        }
      });

      // Cart pill → open cart drawer instead of navigating
      const cartPill = menu.querySelector('[data-grovita-open-cart-drawer]');
      if (cartPill) {
        cartPill.addEventListener('click', () => {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) setTimeout(() => cartDrawer.open(cartPill), 350);
        });
      }

      // Touch-to-dismiss on drag handle
      const dragHandle = menu.querySelector('.grovita-mobile-menu__drag-handle');
      if (dragHandle) {
        let startY = 0;
        dragHandle.addEventListener('touchstart', (e) => {
          startY = e.touches[0].clientY;
        }, { passive: true });
        dragHandle.addEventListener('touchend', (e) => {
          if (e.changedTouches[0].clientY - startY > 40) closeMenu();
        }, { passive: true });
      }

      const handleEscape = (e) => {
        if (e.key === 'Escape' && menu.getAttribute('aria-hidden') === 'false') {
          closeMenu();
        }
      };
      document.addEventListener('keydown', handleEscape);
      container._escapeHandler = handleEscape;
      container._closeMenu = closeMenu;
    }
  },
  destroy: (container) => {
    if (container._scrollHandler) window.removeEventListener('scroll', container._scrollHandler);
    if (container._escapeHandler) document.removeEventListener('keydown', container._escapeHandler);
    if (container._closeMenu) container._closeMenu();
  }
};

// =========================================
// SECTION: CTA BANNER — Cinematic entrance
// =========================================

GrovitaTheme.sections['grovita-cta'] = {
  init: (container) => {
    if (GrovitaTheme.prefersReducedMotion()) return;

    const section = container.closest('.grovita-cta-banner') || container;
    const imagePanel = section.querySelector('.grovita-cta-banner__image-panel');
    const img = section.querySelector('.grovita-cta-banner__img');
    const subtitle = section.querySelector('.grovita-cta-banner__subtitle');
    const heading = section.querySelector('.grovita-cta-banner__heading');
    const desc = section.querySelector('.grovita-cta-banner__desc');
    const btn = section.querySelector('.grovita-cta-banner__btn');

    /* ---- Initial hidden states ---- */

    /* Image panel: scale up slightly + fade */
    if (imagePanel) {
      imagePanel.style.opacity = '0';
      imagePanel.style.transform = 'scale(1.04)';
    }

    /* Subtitle: fade + slight letter-spacing */
    if (subtitle) {
      subtitle.style.opacity = '0';
      subtitle.style.letterSpacing = '0.25em';
      subtitle.style.transform = 'translateY(12px)';
    }

    /* Heading: clip from bottom (curtain reveal) */
    if (heading) {
      heading.style.clipPath = 'inset(100% 0 0 0)';
      heading.style.opacity = '0';
    }

    /* Description: blur-fade */
    if (desc) {
      desc.style.opacity = '0';
      desc.style.transform = 'translateY(16px)';
      desc.style.filter = 'blur(4px)';
    }

    /* Button: scale down + fade */
    if (btn) {
      btn.style.opacity = '0';
      btn.style.transform = 'scale(0.85) translateY(10px)';
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          /* Mark visible (triggers CSS glow + ken-burns) */
          section.classList.add('is-visible');

          /* Image panel: settle into place */
          if (imagePanel) {
            imagePanel.style.transition = 'opacity 0.8s cubic-bezier(0.25,0.46,0.45,0.94), transform 1s cubic-bezier(0.25,0.46,0.45,0.94)';
            imagePanel.style.opacity = '1';
            imagePanel.style.transform = 'scale(1)';
          }

          /* Subtitle: fade in + letter-spacing tightens */
          if (subtitle) {
            subtitle.style.transition = 'opacity 0.6s ease 0.3s, letter-spacing 0.8s ease 0.3s, transform 0.6s ease 0.3s';
            subtitle.style.opacity = '1';
            subtitle.style.letterSpacing = '0.08em';
            subtitle.style.transform = 'translateY(0)';
          }

          /* Heading: curtain reveal upward */
          if (heading) {
            heading.style.transition = 'clip-path 0.7s cubic-bezier(0.77,0,0.18,1) 0.45s, opacity 0.01s linear 0.45s';
            heading.style.clipPath = 'inset(0 0 0 0)';
            heading.style.opacity = '1';
          }

          /* Description: blur clears + fade up */
          if (desc) {
            desc.style.transition = 'opacity 0.6s ease 0.7s, transform 0.6s ease 0.7s, filter 0.6s ease 0.7s';
            desc.style.opacity = '1';
            desc.style.transform = 'translateY(0)';
            desc.style.filter = 'blur(0)';
          }

          /* Button: pop in with bounce */
          if (btn) {
            btn.style.transition = 'opacity 0.5s ease 0.9s, transform 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.9s';
            btn.style.opacity = '1';
            btn.style.transform = 'scale(1) translateY(0)';
          }

          /* Clean up inline styles after all animations finish */
          setTimeout(() => {
            [imagePanel, subtitle, heading, desc, btn].forEach(el => {
              if (el) el.style.cssText = '';
            });
          }, 2000);

          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });

    observer.observe(section);
    container._ctaObserver = observer;
  },
  destroy: (container) => {
    if (container._ctaObserver) container._ctaObserver.disconnect();
    const section = container.closest('.grovita-cta-banner') || container;
    section.classList.remove('is-visible');
  }
};

// =========================================
// SECTION: COLLECTION SHOWCASE — Stagger entrance
// =========================================

GrovitaTheme.sections['grovita-collection-blocks'] = {
  init: (container) => {
    const section = container.closest('.grovita-collection-blocks') || container;
    const cards = section.querySelectorAll('.grovita-cb__card');
    const header = section.querySelector('.grovita-cb__header');

    /* --- Entrance animations via IntersectionObserver --- */
    if (!GrovitaTheme.prefersReducedMotion()) {
      /* Header reveal */
      if (header) {
        header.setAttribute('data-cb-header-reveal', '');
      }

      /* Card stagger reveal */
      cards.forEach(card => {
        card.setAttribute('data-cb-reveal', '');
      });

      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const target = entry.target;

            if (target === section) {
              /* Reveal header first */
              if (header) {
                header.setAttribute('data-cb-header-reveal', 'visible');
              }

              /* Stagger cards */
              cards.forEach((card, i) => {
                setTimeout(() => {
                  card.setAttribute('data-cb-reveal', 'visible');
                }, 200 + i * 150);
              });

              /* Clean up data attributes after animations */
              const cleanupDelay = 200 + (cards.length * 150) + 1000;
              setTimeout(() => {
                if (header) header.removeAttribute('data-cb-header-reveal');
                cards.forEach(card => card.removeAttribute('data-cb-reveal'));
              }, cleanupDelay);

              revealObserver.unobserve(target);
            }
          }
        });
      }, { threshold: 0.1 });

      revealObserver.observe(section);
      section._cbRevealObserver = revealObserver;
    }
  },

  destroy: (container) => {
    const section = container.closest('.grovita-collection-blocks') || container;
    if (section._cbRevealObserver) section._cbRevealObserver.disconnect();
  }
};

// =========================================
// FEATURED PRODUCTS SLIDER — Prev/Next scroll
// Works on all viewport sizes (desktop + mobile).
// Handles [data-grovita-featured-slider] wrappers.
// =========================================

function grovitaInitFeaturedSlider(sliderWrapper) {
  if (!sliderWrapper || sliderWrapper._grovitaFeaturedSliderInit) return;

  const track = sliderWrapper.querySelector('[data-grovita-featured-track]');
  const prevBtn = sliderWrapper.querySelector('[data-grovita-featured-prev]');
  const nextBtn = sliderWrapper.querySelector('[data-grovita-featured-next]');

  if (!track) return;

  function getScrollStep() {
    const firstItem = track.firstElementChild;
    if (!firstItem) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).gap) || 20;
    return firstItem.getBoundingClientRect().width + gap;
  }

  function updateButtons() {
    if (!prevBtn || !nextBtn) return;
    const scrollLeft = Math.round(track.scrollLeft);
    const maxScroll = Math.round(track.scrollWidth - track.clientWidth);
    prevBtn.disabled = scrollLeft <= 1;
    nextBtn.disabled = maxScroll <= 0 || scrollLeft >= maxScroll - 1;
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      track.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      track.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
    });
  }

  track.addEventListener('scroll', updateButtons, { passive: true });

  // Set initial button states after layout is painted
  requestAnimationFrame(() => { requestAnimationFrame(updateButtons); });

  sliderWrapper._grovitaFeaturedSliderInit = true;
}

// =========================================
// SECTION: FEATURED PRODUCTS GRID — Stagger entrance
// =========================================

GrovitaTheme.sections['grovita-horizontal-gallery'] = {
  init: (container) => {
    // Init slider first (works regardless of motion preference)
    const sliderWrapper = container.querySelector('[data-grovita-featured-slider]');
    if (sliderWrapper) grovitaInitFeaturedSlider(sliderWrapper);

    if (GrovitaTheme.prefersReducedMotion()) return;

    const header = container.querySelector('.grovita-featured__header');
    const items = container.querySelectorAll('.grovita-featured__item');
    const footer = container.querySelector('.grovita-featured__footer');

    const allEls = [];
    if (header) allEls.push(header);
    items.forEach(item => allEls.push(item));
    if (footer) allEls.push(footer);

    allEls.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          allEls.forEach((el, i) => {
            el.style.transition = `opacity 0.5s ease ${i * 0.06}s, transform 0.5s ease ${i * 0.06}s`;
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
          });
          setTimeout(() => {
            allEls.forEach(el => { el.style.cssText = ''; });
          }, allEls.length * 60 + 700);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    observer.observe(container);
    container._gridObserver = observer;
  },
  destroy: (container) => {
    if (container._gridObserver) container._gridObserver.disconnect();
  }
};

// =========================================
// SECTION: TRUST FEATURES — Stagger reveal
// =========================================

GrovitaTheme.sections['grovita-features'] = {
  init: (container) => {
    if (GrovitaTheme.prefersReducedMotion()) return;

    const heading = container.querySelector('.grovita-trust__heading');
    const items = container.querySelectorAll('.grovita-trust__item');

    const allEls = [];
    if (heading) allEls.push(heading);
    items.forEach(item => allEls.push(item));

    allEls.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          allEls.forEach((el, i) => {
            el.style.transition = `opacity 0.5s ease ${i * 0.1}s, transform 0.5s ease ${i * 0.1}s`;
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
          });
          setTimeout(() => {
            allEls.forEach(el => { el.style.cssText = ''; });
          }, allEls.length * 100 + 700);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });

    observer.observe(container);
    container._trustObserver = observer;
  },
  destroy: (container) => {
    if (container._trustObserver) container._trustObserver.disconnect();
  }
};

// =========================================
// SECTION: CATEGORY EXPLORER — Stagger reveal
// =========================================

GrovitaTheme.sections['grovita-category-explorer'] = {
  init: (container) => {
    if (GrovitaTheme.prefersReducedMotion()) return;

    const header = container.querySelector('.grovita-catex__header');
    const cards = container.querySelectorAll('.grovita-catex__card');

    // Mark header for reveal
    if (header) header.setAttribute('data-catex-reveal', '');

    // Mark cards for staggered reveal
    cards.forEach(card => card.setAttribute('data-catex-reveal', ''));

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;

        // Reveal header first
        if (header) {
          setTimeout(() => {
            header.setAttribute('data-catex-reveal', 'visible');
          }, 0);
        }

        // Stagger cards
        cards.forEach((card, i) => {
          setTimeout(() => {
            card.setAttribute('data-catex-reveal', 'visible');
          }, (header ? 80 : 0) + i * 130);
        });

        // Cleanup after all animations complete
        const totalDelay = (header ? 80 : 0) + (cards.length - 1) * 130 + 900;
        setTimeout(() => {
          if (header) header.removeAttribute('data-catex-reveal');
          cards.forEach(card => card.removeAttribute('data-catex-reveal'));
        }, totalDelay);

        observer.unobserve(entry.target);
      });
    }, { threshold: 0.1 });

    observer.observe(container);
    container._catexObserver = observer;
  },
  destroy: (container) => {
    if (container._catexObserver) container._catexObserver.disconnect();
  }
};

// =========================================
// SECTION: COMBO REVEAL — GSAP ScrollTrigger scroll storytelling
// Saucer → Pot descends and lands → Combo glow highlight
// Desktop: CSS sticky + GSAP native scrub (scrub: 1.2) — fixes all glitch issues
// Mobile:  IntersectionObserver sequential reveal (no pinning, no GSAP)
// =========================================

(function () {

  // ── Lazy GSAP + ScrollTrigger loader ─────────────────────────────────────────
  // Reuses the same Promise across section re-inits (Theme Editor hot-reload).
  var _gsapReady = null;

  function loadGSAP() {
    if (_gsapReady) return _gsapReady;
    _gsapReady = new Promise(function (resolve, reject) {
      if (window.gsap && window.ScrollTrigger) {
        gsap.registerPlugin(ScrollTrigger);
        resolve();
        return;
      }
      function loadScript(src) {
        return new Promise(function (res, rej) {
          var s = document.createElement('script');
          s.src = src;
          s.onload = res;
          s.onerror = rej;
          document.head.appendChild(s);
        });
      }
      var base = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/';
      (window.gsap ? Promise.resolve() : loadScript(base + 'gsap.min.js'))
        .then(function () {
          return window.ScrollTrigger
            ? Promise.resolve()
            : loadScript(base + 'ScrollTrigger.min.js');
        })
        .then(function () { gsap.registerPlugin(ScrollTrigger); resolve(); })
        .catch(reject);
    });
    return _gsapReady;
  }

  // ── Mobile: IntersectionObserver sequential reveal ────────────────────────────
  function initMobileReveal(container) {
    var anchors  = container.querySelectorAll('[data-cr-mob-anchor]');
    var panel1   = container.querySelector('[data-cr-panel="1"]');
    var panel2   = container.querySelector('[data-cr-panel="2"]');
    var panel3   = container.querySelector('[data-cr-panel="3"]');
    var allPanels = [panel1, panel2, panel3];
    var potEl    = container.querySelector('[data-cr-pot]');
    var saucerEl = container.querySelector('[data-cr-saucer]');

    // CSS centering for saucer and pot (GSAP not loaded on mobile)
    if (saucerEl) saucerEl.style.transform = 'translateX(-50%)';
    if (potEl)    potEl.style.transform    = 'translateX(-50%)';

    function activatePhase(phase) {
      // Update panels
      allPanels.forEach(function(p) {
        if (!p) return;
        var isActive = p.dataset.crPanel === String(phase);
        p.classList.toggle('grovita-cr__panel--visible', isActive);
        p.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
      // Drive CSS pot visibility via data attribute
      container.setAttribute('data-cr-mobile-phase', String(phase));
      // Update dot highlight
      updateProgressDots(container, phase);
      container._crCurrentPhase = phase;
    }

    // Phase 1 is active immediately
    activatePhase(1);

    if (!anchors.length) return; // no anchors = skip observer

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var phase = parseInt(entry.target.dataset.crMobAnchor, 10);
          activatePhase(phase);
        }
      });
    }, {
      // Fires when anchor TOP crosses the TOP HALF of the viewport (scrolling up from below).
      // '-50% on bottom' shrinks the effective zone to the top 50% of screen.
      rootMargin: '0px 0px -50% 0px',
      threshold: 0
    });

    anchors.forEach(function (a) { observer.observe(a); });
    container._crMobileObserver = observer;
  }

  // ── Progress bar fills (called on every scroll tick, lightweight) ─────────────
  function updateProgressFills(container, progress) {
    var fills = container.querySelectorAll('[data-cr-prog-fill]');
    if (!fills.length) return;
    // Bar 0 spans phase 1→2 boundary (0→0.333 scroll progress = 33% of 3-unit timeline)
    if (fills[0]) fills[0].style.height = (Math.min(1, Math.max(0, progress / 0.333)) * 100) + '%';
    // Bar 1 spans phase 2→3 boundary (0.333→0.667 scroll progress)
    if (fills[1]) fills[1].style.height = (Math.min(1, Math.max(0, (progress - 0.333) / 0.334)) * 100) + '%';
  }

  // ── Progress dot highlight (only on phase boundary crossing) ─────────────────
  function updateProgressDots(container, phase) {
    container.querySelectorAll('.grovita-cr__prog-dot').forEach(function (dot) {
      dot.classList.toggle('grovita-cr__prog-dot--active', dot.dataset.phase === String(phase));
    });
  }

  // ── Desktop: unified GSAP timeline with native scrub ─────────────────────────
  //
  // KEY ARCHITECTURAL FIXES applied here:
  //
  //  FIX 1: Use `animation: tl` + `scrub: 1.2` on ScrollTrigger.
  //          GSAP internally runs gsap.to(tl, { progress: target }) every frame,
  //          adding natural lag + weight. No more manual tl.progress(p) on every tick.
  //
  //  FIX 2: Timeline duration proportions match scroll proportions exactly.
  //          Each phase = 1 unit → total = 3 units. Phase X = 33% scroll = 33% timeline.
  //          No more duration-vs-progress math mismatch.
  //
  //  FIX 3: All easing is monotonic (power2.inOut, sine.inOut, expo.out).
  //          No back.out / elastic / bounce — those cause negative progress on reverse scroll.
  //
  //  FIX 4: Panel crossfades baked into the GSAP timeline.
  //          No CSS transition clock conflicts with scroll tick frequency.
  //
  //  FIX 7: Saucer float is a GSAP yoyo tween stored on container._crFloatTween.
  //          kill()ing it stops at the current frame cleanly, then GSAP resets y to 0.
  //
  function initDesktopAnimation(container) {
    var stage        = container.querySelector('[data-cr-stage]');
    var saucerEl     = container.querySelector('[data-cr-saucer]');
    var saucerImg    = container.querySelector('.grovita-cr__saucer-img, .grovita-cr__placeholder--saucer');
    var saucerShadow = container.querySelector('[data-cr-saucer-shadow]');
    var potEl        = container.querySelector('[data-cr-pot]');
    var potShadow    = container.querySelector('[data-cr-pot-shadow]');
    var glowEl       = container.querySelector('[data-cr-glow]');
    var veilEl       = container.querySelector('[data-cr-veil]');
    var panel1       = container.querySelector('[data-cr-panel="1"]');
    var panel2       = container.querySelector('[data-cr-panel="2"]');
    var panel3       = container.querySelector('[data-cr-panel="3"]');

    if (!saucerEl || !potEl) return;

    // ── Hard-set initial states (synchronous, before first paint) ─────────────
    // Saucer: horizontally centered via xPercent (GSAP owns transform).
    // x:0 explicitly overrides any inherited CSS translateX pixel-value that GSAP
    // would otherwise read from the element's computed transform on first touch.
    // Without x:0, mobile CSS  transform:translateX(-50%)  causes GSAP to cache
    // x ≈ -139px and then also apply xPercent:-50 → double offset.
    gsap.set(saucerEl, { xPercent: -50, x: 0 });

    // Pot: GSAP takes full transform ownership. Starting y = -110vh (above viewport).
    // x:0  clears the inherited translateX from CSS  transform:translate(-50%, −100vh−200px)
    // y is set explicitly so GSAP doesn't inherit the CSS y offset either.
    gsap.set(potEl, {
      xPercent: -50,
      x: 0,
      y: -(window.innerHeight * 1.1),
      opacity: 0
    });

    // Shadows and glow: invisible at start
    if (potShadow) gsap.set(potShadow, { opacity: 0, scaleX: 0.12 });
    if (glowEl)   gsap.set(glowEl,   { opacity: 0, scale: 0.82 });
    if (veilEl)   gsap.set(veilEl,   { opacity: 0 });

    // BUG FIX — Panels: use yPercent:-50 for vertical centering (GSAP-owned).
    // gsap.set with y:0 would replace the CSS transform:translateY(-50%), destroying
    // the centering. yPercent:-50 is stored in GSAP's transform cache alongside any
    // future y animations — they compose correctly without conflict.
    //
    // Panel 1 starts VISIBLE (opacity:1) so text is present immediately when
    // the section enters the viewport without requiring scroll.
    // y:0 clears any inherited CSS transform pixel-y (e.g. from mobile panel CSS
    // transform:translateY(-50%)) so yPercent:-50 isn't doubled into translateY(-100%).
    if (panel1) {
      gsap.set(panel1, { yPercent: -50, y: 0, opacity: 1 });
      panel1.classList.add('grovita-cr__panel--visible');
      panel1.removeAttribute('aria-hidden');
    }
    if (panel2) gsap.set(panel2, { yPercent: -50, y: 0, opacity: 0 });
    if (panel3) gsap.set(panel3, { yPercent: -50, y: 0, opacity: 0 });

    // ── Saucer float tween (GSAP yoyo, kill()-able at any frame) ─────────────
    // FIX 7: Replaces CSS animation: cr-float (which freezes at random frame on pause).
    // GSAP kill() stops it cleanly and we can animate y back to 0 in the same frame.
    var floatTween = null;
    if (saucerImg) {
      floatTween = gsap.to(saucerImg, {
        y: -11,
        duration: 2,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1
      });
    }
    container._crFloatTween = floatTween;

    // ── Build the unified scrubbed timeline ───────────────────────────────────
    //
    // Total duration = 3 units (arbitrary; ScrollTrigger normalises to 0–1).
    // Each phase = 1 unit = 33.3% of scroll travel.
    //
    // Timeline map:
    //  0.00 → Phase 1 starts  (saucer only, floating)
    //  0.85 → Panel 1 begins fading out
    //  1.00 → Phase 2 starts  (pot begins descent — power3.out for gravity feel)
    //  1.05 → Panel 2 fades in
    //  1.90 → Pot lands on saucer  (extended from 1.40 for smoother descent)
    //  1.85 → Panel 2 begins fading out
    //  2.00 → Phase 3 starts  (combo highlight)
    //  2.05 → Panel 3 fades in, glow + veil fade in
    //  3.00 → Timeline end
    //
    var tl = gsap.timeline({ defaults: { ease: 'power2.inOut' } });

    // ── PHASE 1: panel 1 already visible (opacity:1 set above via gsap.set) ──
    // Phase 1 spacer — saucer floats, pot hidden, panel1 stays visible
    tl.to({}, { duration: 0.82 });

    // ── PHASE 1 → 2 crossfade ────────────────────────────────────────────────
    // Panel 1 fades out upward; panel 2 fades in from its centered position
    tl.to(panel1, { opacity: 0, y: -18, duration: 0.22 }, 0.82);
    tl.to(panel2, { opacity: 1, y:   0, duration: 0.22 }, 1.00);

    // ── PHASE 2: Pot descends ─────────────────────────────────────────────────
    // power3.out: fast start that decelerates naturally — mimics gravity.
    // duration 0.90 (was 0.55) spreads the fall over more scroll travel → smoother.
    tl.to(potEl, {
      y: 0,
      opacity: 1,
      ease: 'power3.out',
      duration: 0.90
    }, 1.00);

    // Pot shadow grows as pot descends
    if (potShadow) {
      tl.to(potShadow, {
        opacity: 1,
        scaleX: 1,
        ease: 'power3.out',
        duration: 0.90
      }, 1.00);
    }

    // Very slight saucer press-down as pot lands (depth realism, monotonic)
    tl.to(saucerEl, {
      y: 4,
      ease: 'power2.out',
      duration: 0.18
    }, 1.82);

    // ── PHASE 2 → 3 crossfade ────────────────────────────────────────────────
    // Extended duration (0.22→0.38) for a gradual fade, matching phase 1→2 feel.
    tl.to(panel2, { opacity: 0, y: -14, duration: 0.38 }, 1.72);
    tl.to(panel3, { opacity: 1, y:   0, duration: 0.38 }, 2.00);

    // ── PHASE 3: Combo highlight ──────────────────────────────────────────────
    // Glow, veil and scale-back are staggered so they layer in one by one
    // instead of hitting simultaneously — feels orchestrated, not abrupt.

    // Glow ring: first in, generous duration
    if (glowEl) {
      tl.to(glowEl, {
        opacity: 1,
        scale: 1,
        ease: 'power2.out',
        duration: 0.55
      }, 2.02);
    }

    // Warmth veil: slight delay after glow starts
    if (veilEl) {
      tl.to(veilEl, { opacity: 1, duration: 0.55, ease: 'power1.out' }, 2.14);
    }

    // Camera step-back: enters last, slow + smooth so it feels like a gentle pull-back
    tl.to([saucerEl, potEl], {
      scale: 0.94,
      ease: 'power2.inOut',
      duration: 0.60
    }, 2.28);

    // ── End spacer to 3.0 ────────────────────────────────────────────────────
    tl.to({}, { duration: 0.12 });

    container._crTimeline = tl;

    // ── Wire ScrollTrigger — FIX 1 + FIX 2 ──────────────────────────────────
    //
    // `animation: tl` hands full control to ScrollTrigger.
    // `scrub: 1.2` adds 1.2s of internal lag (the "premium weight" feeling).
    //   ScrollTrigger animates tl.progress toward the target value each frame
    //   using its own internal ease — no manual onUpdate math needed.
    //
    var st = ScrollTrigger.create({
      trigger: container,
      start: 'top top',
      end: 'bottom bottom',
      // GSAP pin replaces broken CSS sticky (body/html overflow-x:hidden breaks
      // position:sticky in Chrome). pin:stage uses position:fixed via JS,
      // which works regardless of ancestor overflow settings.
      pin: stage,
      pinSpacing: false,
      animation: tl,
      // scrub: 2.5 — more lag than 1.2, gives the animation a heavier/premium feel.
      // The animation "catches up" to scroll position over 2.5 seconds instead of 1.2.
      scrub: 2.5,
      // Flush all cached start values when viewport changes (e.g. iOS address
      // bar show/hide, orientation change). This ensures xPercent:-50 / y values
      // are recalculated from the live DOM rather than stale cached pixels.
      invalidateOnRefresh: true,

      // onUpdate used ONLY for lightweight progress indicator sync
      onUpdate: function (self) {
        var p = self.progress;

        // Update progress bar fills
        updateProgressFills(container, p);

        // Update dots on phase boundary (only on phase change)
        var phase = p < 0.333 ? 1 : p < 0.667 ? 2 : 3;
        if (container._crCurrentPhase !== phase) {
          container._crCurrentPhase = phase;
          updateProgressDots(container, phase);

          // FIX 7: Kill float tween cleanly when pot starts moving (phase 2+)
          if (phase >= 2 && container._crFloatTween) {
            container._crFloatTween.kill();
            container._crFloatTween = null;
            // Snap saucer image y back to 0 so GSAP saucer press-down starts clean
            if (saucerImg) gsap.to(saucerImg, { y: 0, duration: 0.25, ease: 'power2.out' });
          }
        }
      }
    });

    container._crScrollTrigger = st;
  }

  // ── Section registration ───────────────────────────────────────────────────
  GrovitaTheme.sections['grovita-combo-reveal'] = {

    init: function (container) {
      // ── Reduced motion: show everything immediately, no animation ────────
      if (GrovitaTheme.prefersReducedMotion()) {
        container.querySelectorAll('[data-cr-panel]').forEach(function (p) {
          p.classList.add('grovita-cr__panel--visible');
          p.style.opacity = '1';
          p.removeAttribute('aria-hidden');
        });
        var potEl = container.querySelector('[data-cr-pot]');
        if (potEl) { potEl.style.opacity = '1'; potEl.style.transform = 'translate(-50%, 0)'; }
        return;
      }

      // ── GSAP ScrollTrigger animation on all screen sizes ─────────────────
      //
      // Mobile was previously split into IntersectionObserver, but that approach
      // only gives a basic opacity-fade — no pot-falling animation.
      //
      // Root cause of the old mobile bug was fixed by adding  x:0 / y:0  to all
      // gsap.set() calls: GSAP was reading the CSS  transform:translate(-50%,...)
      // on elements and inheriting the pixel x/y component.  Without explicit 0s,
      // xPercent:-50 PLUS the ≈-139px inherited x produced a double-offset.
      //
      // Additional fixes: invalidateOnRefresh:true (flushes cached values on
      // viewport resize/iOS address-bar toggle) and normalizeScroll for touch
      // (prevents iOS jitter and address-bar size shifts mid-scroll).
      loadGSAP()
        .then(function () {
          initDesktopAnimation(container);
        })
        .catch(function (err) {
          console.warn('[GroVita ComboReveal] GSAP unavailable — using IntersectionObserver fallback.', err);
          initMobileReveal(container);
        });
    },

    destroy: function (container) {
      if (container._crScrollTrigger)  { container._crScrollTrigger.kill();  container._crScrollTrigger = null; }
      if (container._crTimeline)       { container._crTimeline.kill();        container._crTimeline = null; }
      if (container._crFloatTween)     { container._crFloatTween.kill();      container._crFloatTween = null; }
      if (container._crMobileObserver) { container._crMobileObserver.disconnect(); container._crMobileObserver = null; }
      if (container._crResizeHandler)  {
        window.removeEventListener('resize', container._crResizeHandler);
        container._crResizeHandler = null;
      }
    }
  };

}()); // End COMBO REVEAL IIFE

// =========================================
// SECTION: NEWSLETTER — Entrance animation
// =========================================

GrovitaTheme.sections['grovita-newsletter'] = {
  init: (container) => {
    if (GrovitaTheme.prefersReducedMotion()) return;

    const inner = container.querySelector('.grovita-newsletter__inner');
    if (!inner) return;

    inner.style.opacity = '0';
    inner.style.transform = 'translateY(30px)';

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          inner.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
          inner.style.opacity = '1';
          inner.style.transform = 'translateY(0)';
          setTimeout(() => { inner.style.cssText = ''; }, 1000);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });

    observer.observe(container);
    container._nlObserver = observer;
  },
  destroy: (container) => {
    if (container._nlObserver) container._nlObserver.disconnect();
  }
};

// Initialize Grovita Global Object
window.GrovitaTheme = GrovitaTheme;

// =========================================
// GROVITA WISHLIST SYSTEM
// localStorage-based wishlist storing full product data
// =========================================

const GrovitaWishlist = {
  STORAGE_KEY: 'grovita-wishlist',

  getItems() {
    try {
      const raw = JSON.parse(localStorage.getItem(this.STORAGE_KEY)) || [];
      if (raw.length > 0 && typeof raw[0] === 'string') {
        localStorage.removeItem(this.STORAGE_KEY);
        return [];
      }
      return raw;
    } catch { return []; }
  },

  saveItems(items) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    this._broadcast();
  },

  has(productId) {
    return this.getItems().some(item => String(item.id) === String(productId));
  },

  getProduct(productId) {
    return this.getItems().find(item => String(item.id) === String(productId)) || null;
  },

  add(productData) {
    const items = this.getItems();
    if (items.some(item => String(item.id) === String(productData.id))) return false;
    items.push(productData);
    this.saveItems(items);
    return true;
  },

  remove(productId) {
    const items = this.getItems();
    const filtered = items.filter(item => String(item.id) !== String(productId));
    if (filtered.length === items.length) return false;
    this.saveItems(filtered);
    return true;
  },

  toggle(productId, productData) {
    if (this.has(productId)) {
      this.remove(productId);
      return false;
    } else if (productData) {
      this.add(productData);
      return true;
    }
    return false;
  },

  count() {
    return this.getItems().length;
  },

  _broadcast() {
    const count = this.count();

    document.querySelectorAll('[data-grovita-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.closest('[data-grovita-wishlist-bubble]')
        ?.querySelector('.grovita-header__wishlist-count')
        ?.classList.toggle('grovita-hidden', count === 0);
    });

    document.querySelectorAll('.grovita-product-card__heart').forEach(btn => {
      const card = btn.closest('.grovita-product-card');
      if (!card) return;
      const pid = card.getAttribute('data-product-id');
      btn.classList.toggle('is-wishlisted', this.has(pid));
    });

    document.dispatchEvent(new CustomEvent('grovita:wishlist:updated', {
      detail: { items: this.getItems(), count: count }
    }));
  },

  init() {
    this._broadcast();
  }
};

window.GrovitaWishlist = GrovitaWishlist;

// =========================================
// AJAX ADD-TO-CART
// Uses Shopify Section Rendering API to update cart-drawer
// =========================================

function grovitaInitAddToCart() {
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-grovita-add-to-cart]');
    if (!form) return;
    e.preventDefault();
    e.stopImmediatePropagation();

    const btn = form.querySelector('.grovita-product-card__atc-btn');
    if (!btn || btn.classList.contains('is-loading') || btn.classList.contains('is-added')) return;

    btn.classList.add('is-loading');

    const variantId = form.querySelector('input[name="id"]')?.value;
    const quantity = parseInt(form.querySelector('input[name="quantity"]')?.value || '1', 10);

    if (!variantId) {
      console.error('[GroVita] No variant ID found in form');
      btn.classList.remove('is-loading');
      return;
    }

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: parseInt(variantId, 10),
        quantity: quantity,
        sections: ['cart-drawer'],
        sections_url: window.location.pathname,
      }),
    })
      .then(res => {
        if (!res.ok) throw new Error('Network response was not ok (' + res.status + ')');
        return res.json();
      })
      .then(data => {
        if (data.status) {
          console.error('[GroVita] Cart error:', data.description);
          btn.classList.remove('is-loading');
          return;
        }

        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        const atcText = btn.querySelector('.grovita-product-card__atc-text');
        const originalText = atcText ? atcText.textContent : '';
        if (atcText) atcText.textContent = window.variantStrings?.addedToCart || 'Toegevoegd!';

        setTimeout(() => {
          btn.classList.remove('is-added');
          if (atcText) atcText.textContent = originalText;
        }, 2000);

        grovitaUpdateCartCountOptimistic(quantity);
        grovitaUpdateCartCount();

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && data.sections && data.sections['cart-drawer']) {
          const cartDrawerEl = document.getElementById('CartDrawer');
          if (cartDrawerEl) {
            const parsed = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
            const newInner = parsed.querySelector('#CartDrawer');
            if (newInner) cartDrawerEl.innerHTML = newInner.innerHTML;
          }
          const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', () => cartDrawer.close());
          const drawerInner = cartDrawer.querySelector('.drawer__inner');
          if (drawerInner) drawerInner.classList.remove('is-empty');
          cartDrawer.classList.remove('is-empty');
          setTimeout(() => {
            cartDrawer.classList.add('animate', 'active');
            document.body.classList.add('overflow-hidden');
          });
        } else if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          window.location = window.routes.cart_url;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'grovita-product-card',
            productVariantId: variantId,
            cartData: data,
          });
        }
      })
      .catch(err => {
        console.error('[GroVita] Add to cart failed:', err);
        btn.classList.remove('is-loading');
      });
  });
}

// Update cart count via AJAX
function grovitaUpdateCartCount() {
  fetch(window.routes.cart_url + '.js?t=' + Date.now(), {
    credentials: 'same-origin',
    headers: { 'Cache-Control': 'no-cache' },
  })
    .then(r => {
      if (!r.ok) throw new Error('Cart fetch failed: ' + r.status);
      return r.json();
    })
    .then(cart => {
      grovitaSetCartCount(cart.item_count);
    })
    .catch(err => {
      console.error('[GroVita] Cart count update failed:', err);
    });
}

function grovitaSetCartCount(count) {
  document.querySelectorAll('.grovita-header__cart-count').forEach(el => {
    el.textContent = count;
    el.classList.toggle('grovita-badge-hidden', count === 0);
  });
  document.querySelectorAll('[data-grovita-menu-cart-count]').forEach(el => {
    el.textContent = count;
    el.classList.toggle('grovita-badge-hidden', count === 0);
  });
}

function grovitaUpdateCartCountOptimistic(delta) {
  document.querySelectorAll('.grovita-header__cart-count, [data-grovita-menu-cart-count]').forEach(el => {
    const current = parseInt(el.textContent, 10) || 0;
    const newCount = Math.max(0, current + delta);
    el.textContent = newCount;
    el.classList.toggle('grovita-badge-hidden', newCount === 0);
  });
}

// =========================================
// CART ICON — open drawer on click + PubSub sync
// =========================================

function grovitaInitCartIcon() {
  const cartIcon = document.getElementById('grovita-cart-icon');
  if (!cartIcon) return;

  // Check for cart-drawer at CLICK TIME (not init time)
  // Dawn's cart-drawer custom element may not be upgraded yet at DOMContentLoaded
  cartIcon.addEventListener('click', (e) => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      e.preventDefault();
      cartDrawer.open(cartIcon);
    }
    // If cart-drawer not available, the <a> navigates to /cart naturally
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      grovitaUpdateCartCount();
    });
  }

  const cartDrawerEl = document.getElementById('CartDrawer');
  if (cartDrawerEl) {
    let mutationTimer = null;
    const observer = new MutationObserver(() => {
      if (mutationTimer) clearTimeout(mutationTimer);
      mutationTimer = setTimeout(() => {
        grovitaUpdateCartCount();
      }, 300);
    });
    observer.observe(cartDrawerEl, { childList: true, subtree: true });
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) grovitaUpdateCartCount();
  });
}

// =========================================
// THUMBNAIL IMAGE SWAP
// =========================================

function grovitaInitThumbnails(scope) {
  const root = scope || document;
  root.querySelectorAll('.grovita-product-card__thumb').forEach(thumb => {
    thumb.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const card = thumb.closest('.grovita-product-card');
      if (!card) return;
      const mainImg = card.querySelector('.grovita-product-card__image');
      const thumbImg = thumb.querySelector('img');
      if (mainImg && thumbImg) {
        const newSrc = thumbImg.src.replace(/width=\d+/, 'width=533');
        mainImg.src = newSrc;
        mainImg.srcset = newSrc + ' 533w';
      }
      card.querySelectorAll('.grovita-product-card__thumb').forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });
}

// =========================================
// WISHLIST CLICK HANDLER
// =========================================

function grovitaInitWishlistButtons(scope) {
  const root = scope || document;
  root.querySelectorAll('.grovita-product-card__heart').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const card = btn.closest('.grovita-product-card');
      if (!card) return;
      const productId = card.getAttribute('data-product-id');
      if (!productId) return;

      const titleEl = card.querySelector('.grovita-product-card__title');
      const linkEl = card.querySelector('.grovita-product-card__link');
      const priceEl = card.querySelector('.grovita-product-card__price-current');
      const imgEl = card.querySelector('.grovita-product-card__image');
      const variantInput = card.querySelector('input[name="id"]');

      const productData = {
        id: String(productId),
        title: titleEl ? titleEl.textContent.trim() : 'Product',
        url: linkEl ? linkEl.getAttribute('href') : '/products/',
        price: priceEl ? priceEl.textContent.trim() : '',
        image: imgEl ? imgEl.getAttribute('src') : '',
        variant_id: variantInput ? variantInput.value : null,
        available: true
      };

      GrovitaWishlist.toggle(productId, productData);

      btn.style.transform = 'scale(1.3)';
      setTimeout(() => { btn.style.transform = ''; }, 200);
    });
  });
}

// =========================================
// WISHLIST DRAWER CONTROLLER
// =========================================

const GrovitaWishlistDrawer = {
  _drawer: null,
  _body: null,
  _items: null,
  _empty: null,
  _loading: null,
  _footer: null,
  _count: null,

  init() {
    this._drawer = document.getElementById('GrovitaWishlistDrawer');
    if (!this._drawer) return;

    this._body = this._drawer.querySelector('[data-grovita-wishlist-drawer-body]');
    this._items = this._drawer.querySelector('[data-grovita-wishlist-drawer-items]');
    this._empty = this._drawer.querySelector('[data-grovita-wishlist-drawer-empty]');
    this._loading = this._drawer.querySelector('[data-grovita-wishlist-drawer-loading]');
    this._footer = this._drawer.querySelector('[data-grovita-wishlist-drawer-footer]');
    this._count = this._drawer.querySelector('[data-grovita-wishlist-drawer-count]');

    document.querySelectorAll('[data-grovita-wishlist-drawer-open]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this._drawer.querySelectorAll('[data-grovita-wishlist-drawer-close]').forEach(el => {
      el.addEventListener('click', () => this.close());
    });

    // Robust outside-click: close when clicking anywhere outside the panel.
    // (Belt-and-braces alongside the overlay handler above.)
    document.addEventListener('click', (e) => {
      if (!this._drawer.classList.contains('is-open')) return;
      const panel = this._drawer.querySelector('.grovita-wishlist-drawer__panel');
      if (panel && !panel.contains(e.target) && !e.target.closest('[data-grovita-wishlist-drawer-open]')) {
        this.close();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._drawer.classList.contains('is-open')) {
        this.close();
      }
    });

    const clearBtn = this._drawer.querySelector('[data-grovita-wishlist-clear-all]');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        GrovitaWishlist.saveItems([]);
        GrovitaWishlist._broadcast();
        this._renderFromStorage();
      });
    }

    document.addEventListener('grovita:wishlist:updated', () => {
      if (this._drawer.classList.contains('is-open')) {
        this._renderFromStorage();
      }
    });
  },

  open() {
    if (!this._drawer) return;
    this._drawer.classList.add('is-open');
    this._drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('grovita-wishlist-drawer-open');
    this._renderFromStorage();
  },

  close() {
    if (!this._drawer) return;
    this._drawer.classList.remove('is-open');
    this._drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('grovita-wishlist-drawer-open');
  },

  _renderFromStorage() {
    const items = GrovitaWishlist.getItems();

    if (this._count) {
      this._count.textContent = '(' + items.length + ')';
    }

    if (this._loading) this._loading.style.display = 'none';

    if (items.length === 0) {
      this._showEmpty();
      return;
    }

    if (this._empty) this._empty.style.display = 'none';
    this._renderItems(items);
  },

  _showEmpty() {
    if (this._loading) this._loading.style.display = 'none';
    if (this._items) this._items.innerHTML = '';
    if (this._empty) this._empty.style.display = '';
    if (this._footer) this._footer.style.display = 'none';
  },

  _renderItems(products) {
    if (products.length === 0) {
      this._showEmpty();
      return;
    }

    if (this._empty) this._empty.style.display = 'none';
    if (this._footer) this._footer.style.display = '';

    const html = products.map(p => {
      const priceDisplay = p.price || '';
      const atcLabel = window.variantStrings?.addToCart || 'In wagen';

      return `
        <li class="grovita-wishlist-drawer__item" data-wishlist-product-id="${p.id}">
          <div class="grovita-wishlist-drawer__item-image">
            <a href="${p.url}" tabindex="-1">
              ${p.image ? '<img src="' + this._escapeHtml(p.image) + '" alt="' + this._escapeHtml(p.title) + '" width="72" height="72" loading="lazy">' : ''}
            </a>
          </div>
          <div class="grovita-wishlist-drawer__item-body">
            <div class="grovita-wishlist-drawer__item-info">
              <h3 class="grovita-wishlist-drawer__item-title">
                <a href="${p.url}">${this._escapeHtml(p.title)}</a>
              </h3>
              ${priceDisplay ? '<span class="grovita-wishlist-drawer__item-price">' + this._escapeHtml(priceDisplay) + '</span>' : ''}
            </div>
            <div class="grovita-wishlist-drawer__item-actions">
              ${p.variant_id ? `
                <button type="button" class="grovita-wishlist-drawer__item-atc" data-variant-id="${p.variant_id}">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                  <span class="grovita-wishlist-drawer__atc-text">${this._escapeHtml(atcLabel)}</span>
                </button>
              ` : '<span class="grovita-wishlist-drawer__sold-out">Uitverkocht</span>'}
              <button type="button" class="grovita-wishlist-drawer__item-remove" data-remove-id="${p.id}" aria-label="Verwijderen">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>
        </li>
      `;
    }).join('');

    if (this._items) {
      this._items.innerHTML = html;
      this._bindItemActions();
    }
  },

  _bindItemActions() {
    this._items.querySelectorAll('[data-remove-id]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const id = btn.getAttribute('data-remove-id');
        GrovitaWishlist.remove(id);
        const li = btn.closest('.grovita-wishlist-drawer__item');
        if (li) {
          li.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
          li.style.opacity = '0';
          li.style.transform = 'translateX(20px)';
          setTimeout(() => {
            li.remove();
            if (GrovitaWishlist.count() === 0) this._showEmpty();
            if (this._count) {
              const c = GrovitaWishlist.count();
              this._count.textContent = c > 0 ? '(' + c + ')' : '';
            }
          }, 250);
        }
      });
    });

    this._items.querySelectorAll('[data-variant-id]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;

        const textEl = btn.querySelector('.grovita-wishlist-drawer__atc-text');
        const originalText = textEl ? textEl.textContent : '';
        btn.classList.add('is-loading');
        if (textEl) textEl.textContent = '...';

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: parseInt(variantId, 10),
            quantity: 1,
            sections: ['cart-drawer'],
            sections_url: window.location.pathname,
          }),
        })
          .then(r => {
            if (!r.ok) throw new Error('Failed');
            return r.json();
          })
          .then(data => {
            btn.classList.remove('is-loading');
            btn.classList.add('is-added');
            if (textEl) textEl.textContent = 'Toegevoegd ✓';

            setTimeout(() => {
              btn.classList.remove('is-added');
              if (textEl) textEl.textContent = originalText;
            }, 2000);

            grovitaUpdateCartCountOptimistic(1);
            grovitaUpdateCartCount();

            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && data.sections && data.sections['cart-drawer']) {
              const cartDrawerEl = document.getElementById('CartDrawer');
              if (cartDrawerEl) {
                const parsed = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
                const newInner = parsed.querySelector('#CartDrawer');
                if (newInner) cartDrawerEl.innerHTML = newInner.innerHTML;
              }
              cartDrawer.classList.remove('is-empty');
              const drawerInner = cartDrawer.querySelector('.drawer__inner');
              if (drawerInner) drawerInner.classList.remove('is-empty');
              const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
              if (overlay) overlay.addEventListener('click', () => cartDrawer.close());
            }
          })
          .catch(() => {
            btn.classList.remove('is-loading');
            if (textEl) textEl.textContent = originalText;
          });
      });
    });
  },

  _escapeHtml(str) {
    if (str == null) return '';
    const div = document.createElement('div');
    div.textContent = String(str);
    return div.innerHTML;
  }
};

window.GrovitaWishlistDrawer = GrovitaWishlistDrawer;

// =========================================
// MOBILE SCROLL SLIDERS — Prev/Next Buttons
// Handles [data-grovita-slider] wrappers.
// Works with any track that has [data-grovita-slider-track].
// Only activates on mobile (≤ 749px); no-ops on desktop.
// =========================================

function initGrovitaSlider(wrapper) {
  if (!wrapper || wrapper._grovitaSliderInit) return;

  const track = wrapper.querySelector('[data-grovita-slider-track]');
  const prevBtn = wrapper.querySelector('[data-grovita-slider-prev]');
  const nextBtn = wrapper.querySelector('[data-grovita-slider-next]');

  if (!track || !prevBtn || !nextBtn) return;

  // NB: we always wire the buttons up (the CSS hides them on desktop, where
  // the track is a static grid). The previous `innerWidth > 749` early-return
  // meant that a page first laid out at desktop width — or any timing where
  // init ran before the mobile layout applied — left the arrows permanently
  // dead, even after resizing/rotating into the mobile breakpoint.

  function getScrollStep() {
    const firstCard = track.firstElementChild;
    if (!firstCard) return track.clientWidth / 2;
    // card width + gap (12px from CSS)
    return firstCard.getBoundingClientRect().width + 12;
  }

  function updateButtons() {
    const scrollLeft = track.scrollLeft;
    const maxScroll = track.scrollWidth - track.clientWidth;
    prevBtn.disabled = scrollLeft <= 2;
    nextBtn.disabled = scrollLeft >= maxScroll - 2;
  }

  prevBtn.addEventListener('click', function () {
    track.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
  });

  nextBtn.addEventListener('click', function () {
    track.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
  });

  track.addEventListener('scroll', updateButtons, { passive: true });

  // Re-check on resize (width changes the scrollable amount)
  var _lastRV = window.innerWidth;
  window.addEventListener('resize', function () {
    var vw = window.innerWidth;
    if (vw !== _lastRV) {
      _lastRV = vw;
      updateButtons();
    }
  });

  // Set initial disabled state — once now and once after layout/images settle
  // so the "next" arrow isn't wrongly disabled before the cards have width.
  updateButtons();
  requestAnimationFrame(updateButtons);
  window.addEventListener('load', updateButtons);

  // Mark as initialized to prevent double-init
  wrapper._grovitaSliderInit = true;
}

// Expose globally so the recently-viewed inline script can call it after async render
window.GrovitaSlider = { init: initGrovitaSlider };

// Init sliders for the related-products section (uses product-recommendations
// web component that fetches content via AJAX — observe when content is injected)
function initProductRecommendationsSliders(root) {
  const scope = root || document;
  scope.querySelectorAll('product-recommendations').forEach(function (elem) {
    // Check if already rendered
    const existing = elem.querySelector('[data-grovita-slider]');
    if (existing) {
      initGrovitaSlider(existing);
      return;
    }
    // Observe for AJAX content injection
    const observer = new MutationObserver(function (mutations, obs) {
      const slider = elem.querySelector('[data-grovita-slider]');
      if (slider) {
        initGrovitaSlider(slider);
        obs.disconnect();
      }
    });
    observer.observe(elem, { childList: true, subtree: true });
  });
}

/* ============================================================
   GROVITA COLLECTION — Grid View Switcher
   Persists the column choice across AJAX filter/sort updates
   because data-view lives on #ProductGridContainer (which
   facets.js never replaces, only its innerHTML changes).
   ============================================================ */

const GROVITA_VIEW_KEY = 'grovita-collection-view';

function grovitaInitCollectionViewSwitch(scope) {
  const root = scope || document;
  const gridWrap = root.getElementById('ProductGridContainer')
    || document.getElementById('ProductGridContainer');
  if (!gridWrap) return;

  // Use the container element so event delegation works after AJAX re-renders
  const container = root.querySelector('[data-view-switch]')
    || document.querySelector('[data-view-switch]');
  if (!container || container._grovitaViewInit) return;
  container._grovitaViewInit = true;

  // Restore saved view (default: 4 on desktop, 2 on mobile)
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const defaultView = isMobile ? '2' : '4';
  const saved = localStorage.getItem(GROVITA_VIEW_KEY) || defaultView;

  // Always re-queries buttons so it works after any cloning/AJAX
  function applyView(view) {
    gridWrap.dataset.view = view;
    container.querySelectorAll('[data-view-btn]').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.viewBtn === view);
    });
  }

  applyView(saved);

  // Single delegated listener on the container — survives AJAX
  container.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-view-btn]');
    if (!btn) return;
    const view = btn.dataset.viewBtn;
    applyView(view);
    localStorage.setItem(GROVITA_VIEW_KEY, view);
  });
}

/* ============================================================
   COLLECTION - Custom filter trigger
   Grovita Filter Drawer — slides from left, same pattern as
   cart-drawer and wishlist-drawer. Uses simple is-open class
   toggle on #GrovitaFilterDrawer. Dawn's facets.js AJAX is
   fully preserved — it re-renders #main-collection-filters
   regardless of its position in the DOM.
   ============================================================ */

function grovitaInitFilterTrigger() {
  const triggerBtn = document.querySelector('[data-grovita-filter-trigger]');
  if (!triggerBtn || triggerBtn._grovitaFilterTriggerInit) return;
  triggerBtn._grovitaFilterTriggerInit = true;

  const drawer = document.getElementById('GrovitaFilterDrawer');
  if (!drawer) return;

  const overlay = drawer.querySelector('[data-filter-overlay]');
  const closeBtn = drawer.querySelector('[data-filter-close]');

  function isDrawerOpen() {
    return drawer.classList.contains('is-open');
  }

  function openDrawer() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('grovita-filter-open');
    triggerBtn.setAttribute('aria-expanded', 'true');
    triggerBtn.classList.add('is-active');
    // Focus the close button for accessibility
    setTimeout(() => { if (closeBtn) closeBtn.focus(); }, 400);
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('grovita-filter-open');
    triggerBtn.setAttribute('aria-expanded', 'false');
    triggerBtn.classList.remove('is-active');
    triggerBtn.focus();
  }

  // Trigger button toggles drawer
  triggerBtn.addEventListener('click', () => {
    isDrawerOpen() ? closeDrawer() : openDrawer();
  });

  // Overlay click closes drawer
  if (overlay) overlay.addEventListener('click', closeDrawer);

  // Robust outside-click: close when clicking anywhere outside the panel.
  document.addEventListener('click', (e) => {
    if (!isDrawerOpen()) return;
    const panel = drawer.querySelector('.grovita-filter-drawer__panel');
    if (panel && !panel.contains(e.target) && !triggerBtn.contains(e.target)) {
      closeDrawer();
    }
  });

  // Close button
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

  // Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isDrawerOpen()) closeDrawer();
  });
}

/* ============================================================
   COLLECTION - Active filter count badge
   Reads the number of active filter pills from the AJAX-replaced
   aside and updates the count badge on the filter trigger button.
   ============================================================ */

function grovitaUpdateFilterCount() {
  const aside   = document.getElementById('main-collection-filters');
  const countEl = document.querySelector('.grovita-filter-count');
  if (!countEl) return;

  const n = aside
    ? aside.querySelectorAll('.active-facets__button--light').length
    : 0;

  countEl.textContent = n > 0 ? String(n) : '';
  countEl.classList.toggle('grovita-filter-count--visible', n > 0);
}

function grovitaObserveFilterChanges() {
  const aside = document.getElementById('main-collection-filters');
  if (!aside || aside._grovitaFilterObserver) return;

  const observer = new MutationObserver(grovitaUpdateFilterCount);
  observer.observe(aside, { childList: true, subtree: true });
  aside._grovitaFilterObserver = observer;

  // Initial count
  grovitaUpdateFilterCount();
}

// Re-trigger reveal animations after Dawn's AJAX filter/sort replaces the product grid
function grovitaInitCollectionGridReveal() {
  const container = document.getElementById('ProductGridContainer');
  if (!container || container._grovitaGridRevealObserver) return;

  const mo = new MutationObserver(() => {
    // After AJAX replaces the grid, find the new <ul> and observe it
    requestAnimationFrame(() => {
      grovitaInitRevealObserver(container);
    });
  });

  mo.observe(container, { childList: true });
  container._grovitaGridRevealObserver = mo;
}

/* ============================================================
   COLLECTION - Custom sort dropdown
   Builds a styled dropdown over the hidden native <select>.
   The native select still drives Dawn's facet-filters-form AJAX.
   ============================================================ */

function grovitaInitSortDropdown() {
  const wrapper = document.getElementById('grovita-sort-container');
  if (!wrapper || wrapper.dataset.sortInit) return;
  wrapper.dataset.sortInit = 'true';

  const select = wrapper.querySelector('select[name="sort_by"]');
  if (!select) return;

  // Build custom dropdown element
  const dropdown = document.createElement('div');
  dropdown.className = 'grovita-sort-dropdown';
  dropdown.innerHTML =
    '<button type="button" class="grovita-sort-trigger" aria-haspopup="listbox" aria-expanded="false">' +
      '<span class="grovita-sort-label"></span>' +
      '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>' +
    '</button>' +
    '<ul class="grovita-sort-panel" role="listbox"></ul>';

  const trigger = dropdown.querySelector('.grovita-sort-trigger');
  const label   = dropdown.querySelector('.grovita-sort-label');
  const panel   = dropdown.querySelector('.grovita-sort-panel');

  function syncLabel() {
    label.textContent = 'Sorteer';
  }

  function buildOptions() {
    panel.innerHTML = '';
    Array.from(select.options).forEach(opt => {
      const li = document.createElement('li');
      li.className = 'grovita-sort-option' + (opt.selected ? ' selected' : '');
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
      li.dataset.value = opt.value;
      li.textContent = opt.text;
      panel.appendChild(li);
    });
    syncLabel();
  }

  buildOptions();

  // Toggle open/close
  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    const isOpen = dropdown.classList.contains('open');
    dropdown.classList.toggle('open', !isOpen);
    trigger.setAttribute('aria-expanded', String(!isOpen));
  });

  // Select an option
  panel.addEventListener('click', (e) => {
    const li = e.target.closest('.grovita-sort-option');
    if (!li) return;

    // Update native select (triggers Dawn's AJAX via 'input' event)
    select.value = li.dataset.value;
    select.dispatchEvent(new Event('input', { bubbles: true }));

    // Update UI
    label.textContent = li.textContent;
    panel.querySelectorAll('.grovita-sort-option').forEach(el => {
      el.classList.toggle('selected', el === li);
      el.setAttribute('aria-selected', String(el === li));
    });

    dropdown.classList.remove('open');
    trigger.setAttribute('aria-expanded', 'false');
  });

  // Close on outside click
  document.addEventListener('click', (e) => {
    if (!dropdown.contains(e.target)) {
      dropdown.classList.remove('open');
      trigger.setAttribute('aria-expanded', 'false');
    }
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && dropdown.classList.contains('open')) {
      dropdown.classList.remove('open');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    }
  });

  wrapper.appendChild(dropdown);
}

document.addEventListener('DOMContentLoaded', () => {
  // Initialize all sections with data-section-type
  document.querySelectorAll('[data-section-type]').forEach(container => {
    const type = container.getAttribute('data-section-type');
    GrovitaTheme.register(type, container);
  });

  // Initialize card interactions
  grovitaInitAddToCart();
  grovitaInitThumbnails();
  grovitaInitWishlistButtons();
  grovitaInitCartIcon();

  // Initialize wishlist
  GrovitaWishlist.init();
  GrovitaWishlistDrawer.init();

  // Initialize reveal animations
  grovitaInitRevealObserver();

  // Sync cart count badge on page load
  grovitaUpdateCartCount();

  // Init mobile sliders (related products via product-recommendations AJAX)
  initProductRecommendationsSliders();

  // Collection grid view switcher
  grovitaInitCollectionViewSwitch();
  // Collection filter trigger + sort dropdown + filter count
  grovitaInitFilterTrigger();
  grovitaInitSortDropdown();
  grovitaObserveFilterChanges();
  // Re-trigger reveal animations after AJAX filter/sort replaces the grid
  grovitaInitCollectionGridReveal();
});

document.addEventListener('shopify:section:load', (e) => {
  // e.target is the Shopify wrapper div — look for [data-section-type] inside it OR on it
  const container = e.target.getAttribute('data-section-type')
    ? e.target
    : e.target.querySelector('[data-section-type]');
  if (container) {
    const type = container.getAttribute('data-section-type');
    GrovitaTheme.register(type, container);
  }
  grovitaInitThumbnails(e.target);
  grovitaInitWishlistButtons(e.target);
  grovitaInitRevealObserver(e.target);
  grovitaInitAddToCart(e.target);
  GrovitaWishlist.init();
  initProductRecommendationsSliders(e.target);
  grovitaInitCollectionViewSwitch(e.target);
  grovitaInitFilterTrigger();
  grovitaInitSortDropdown();
  grovitaObserveFilterChanges();
});

document.addEventListener('shopify:section:unload', (e) => {
  const container = e.target.getAttribute('data-section-type')
    ? e.target
    : e.target.querySelector('[data-section-type]');
  if (container) {
    const type = container.getAttribute('data-section-type');
    GrovitaTheme.unload(type, container);
  }
});

/* ========================================
   COLLECTION LIST PAGE — grovita-list-collections
   Scroll-reveal for collection cards + schema padding injection
   ======================================== */
(function () {
  'use strict';

  const SECTION_TYPE = 'grovita-list-collections';

  /**
   * Apply padding-top / padding-bottom from schema settings.
   * The schema stores pt/pb as integers (px) in data- attrs we set via inline style
   * We inject them as CSS custom properties on the section root.
   */
  function applyPadding(section) {
    // section.settings are not accessible in JS — they come through via schema-rendered
    // inline style blocks. We read the parsed style attribute written by the Liquid
    // {% style %} block (if set). Fallback is fine — CSS defaults handle it.
    // This function intentionally left minimal; Liquid {% style %} handles the actual padding.
  }

  /**
   * Set up IntersectionObserver for .grovita-cl-reveal items.
   * Respects prefers-reduced-motion.
   */
  function initReveal(section) {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      section.querySelectorAll('.grovita-cl-reveal').forEach(el => el.classList.add('is-visible'));
      return null;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.08 }
    );

    section.querySelectorAll('.grovita-cl-reveal').forEach(el => observer.observe(el));
    return observer;
  }

  function destroyReveal(observer) {
    if (observer) observer.disconnect();
  }

  // Store observer refs keyed by section id
  const observerMap = {};

  function initSection(section) {
    if (section.getAttribute('data-section-type') !== SECTION_TYPE) return;
    const id = section.getAttribute('data-section-id');
    observerMap[id] = initReveal(section);
  }

  function destroySection(section) {
    if (section.getAttribute('data-section-type') !== SECTION_TYPE) return;
    const id = section.getAttribute('data-section-id');
    destroyReveal(observerMap[id]);
    delete observerMap[id];
  }

  // Boot on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-section-type="' + SECTION_TYPE + '"]').forEach(initSection);
  });

  // Shopify Theme Editor lifecycle
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-section-type="' + SECTION_TYPE + '"]');
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector('[data-section-type="' + SECTION_TYPE + '"]');
    if (section) destroySection(section);
  });

}());
